import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, TrendingUp, Users, DollarSign, AlertCircle, Download, Calendar, Filter } from 'lucide-react';

const PPPoEReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  // Estados para resumen y vencidos
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState(null);
  
  // Estados para pagos
  const [payments, setPayments] = useState([]);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [clients, setClients] = useState([]);
  
  // Estados de filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedMethod, setSelectedMethod] = useState('all');
  
  // Estados UI
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, payments, overdue

  useEffect(() => {
    loadReports();
    loadClients();
    
    // Establecer fechas por defecto (último mes)
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    setStartDate(lastMonth.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  const loadReports = async () => {
    try {
      const [summaryRes, overdueRes] = await Promise.all([
        api.get('/api/pppoe/reports/summary'),
        api.get('/api/pppoe/reports/overdue')
      ]);
      setSummary(summaryRes.data);
      setOverdue(overdueRes.data);
    } catch (error) {
      console.error('Error cargando reportes:', error);
      alert('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await api.get('/api/pppoe/clients');
      setClients(res.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedClient && selectedClient !== 'all') params.append('client_id', selectedClient);
      if (selectedMethod && selectedMethod !== 'all') params.append('payment_method', selectedMethod);
      
      const res = await api.get(`/api/pppoe/reports/payments?${params.toString()}`);
      setPayments(res.data.payments);
      setPaymentsSummary(res.data.summary);
    } catch (error) {
      console.error('Error cargando pagos:', error);
      alert('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (payments.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Crear CSV
    const headers = ['Fecha', 'Cliente', 'Plan', 'Monto', 'Método', 'Referencia', 'Notas', 'Registrado por'];
    const rows = payments.map(p => [
      new Date(p.payment_date).toLocaleDateString('es-MX'),
      p.client_name,
      p.plan_name,
      `$${p.amount.toLocaleString('es-MX')}`,
      p.payment_method === 'cash' ? 'Efectivo' : 'Transferencia',
      p.reference || '-',
      p.notes || '-',
      p.registered_by_name
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pagos_pppoe_${startDate}_${endDate}.csv`;
    link.click();
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pppoe')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Reportes PPPoE
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Estadísticas y análisis de clientes PPPoE
              </p>
            </div>
          </div>
          
          <button
            onClick={async () => {
              if (!window.confirm('¿Ejecutar revisión de suspensiones ahora?\n\nEsto suspenderá todos los clientes morosos que hayan pasado el periodo de gracia.')) return;
              
              setLoading(true);
              try {
                const response = await api.post('/api/pppoe/suspensions/check-now');
                alert(`✅ Revisión completada\n\n` +
                      `Clientes revisados: ${response.data.clients_reviewed}\n` +
                      `Clientes suspendidos: ${response.data.clients_suspended}\n` +
                      `Errores: ${response.data.errors}`);
                loadReports();
              } catch (error) {
                console.error('Error ejecutando revisión:', error);
                alert('❌ Error al ejecutar revisión de suspensiones');
              } finally {
                setLoading(false);
              }
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5" />
            Revisar Suspensiones
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className={`flex gap-2 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'overview'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-5 h-5 inline mr-2" />
              Resumen General
            </button>
            <button
              onClick={() => {
                setActiveTab('payments');
                if (payments.length === 0) loadPayments();
              }}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'payments'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <DollarSign className="w-5 h-5 inline mr-2" />
              Pagos y Abonos
            </button>
            <button
              onClick={() => setActiveTab('overdue')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'overdue'
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <AlertCircle className="w-5 h-5 inline mr-2" />
              Clientes Vencidos
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && summary && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8" />
                </div>
                <div className="text-3xl font-bold mb-1">{summary.total_clients}</div>
                <div className="text-blue-100">Total Clientes</div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8" />
                </div>
                <div className="text-3xl font-bold mb-1">{summary.active_clients}</div>
                <div className="text-green-100">Clientes Activos</div>
              </div>

              <div className="bg-gradient-to-br from-red-500 to-pink-500 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <AlertCircle className="w-8 h-8" />
                </div>
                <div className="text-3xl font-bold mb-1">{summary.suspended_clients}</div>
                <div className="text-red-100">Suspendidos</div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8" />
                </div>
                <div className="text-3xl font-bold mb-1">
                  ${summary.monthly_income.toLocaleString('es-MX')}
                </div>
                <div className="text-purple-100">Ingresos del Mes</div>
              </div>
            </div>

            <div className={`rounded-xl shadow-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Resumen del Mes Actual
              </h3>
              <div className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="mb-2">• {summary.payments_count} pagos registrados este mes</p>
                <p className="mb-2">• Ingreso promedio por pago: ${(summary.monthly_income / (summary.payments_count || 1)).toLocaleString('es-MX', {maximumFractionDigits: 0})}</p>
                <p>• Tasa de cobranza: {((summary.active_clients / (summary.total_clients || 1)) * 100).toFixed(1)}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {/* Filters */}
            <div className={`rounded-xl shadow-lg p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Filter className="w-5 h-5 inline mr-2" />
                Filtros
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Cliente
                  </label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="all">Todos los clientes</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Método de Pago
                  </label>
                  <select
                    value={selectedMethod}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="all">Todos los métodos</option>
                    <option value="cash">Efectivo</option>
                    <option value="transfer">Transferencia</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 mt-4">
                <button
                  onClick={loadPayments}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-xl transition-all"
                >
                  Buscar
                </button>
                <button
                  onClick={exportToCSV}
                  disabled={payments.length === 0}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5" />
                  Exportar CSV
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            {paymentsSummary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl shadow-lg p-6 text-white">
                  <div className="text-2xl font-bold mb-1">{paymentsSummary.total_payments}</div>
                  <div className="text-purple-100">Total Pagos</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg p-6 text-white">
                  <div className="text-2xl font-bold mb-1">
                    ${paymentsSummary.total_amount.toLocaleString('es-MX')}
                  </div>
                  <div className="text-green-100">Total Recaudado</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg p-6 text-white">
                  <div className="text-2xl font-bold mb-1">
                    ${paymentsSummary.cash_amount.toLocaleString('es-MX')}
                  </div>
                  <div className="text-blue-100">Efectivo ({paymentsSummary.cash_payments})</div>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl shadow-lg p-6 text-white">
                  <div className="text-2xl font-bold mb-1">
                    ${paymentsSummary.transfer_amount.toLocaleString('es-MX')}
                  </div>
                  <div className="text-orange-100">Transferencias ({paymentsSummary.transfer_payments})</div>
                </div>
              </div>
            )}

            {/* Payments Table */}
            <div className={`rounded-xl shadow-lg overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Fecha
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Cliente
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Plan
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Monto
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Método
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Referencia
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {payments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className={`px-6 py-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {loading ? 'Cargando...' : 'No hay pagos en el rango seleccionado. Usa los filtros para buscar.'}
                        </td>
                      </tr>
                    ) : (
                      payments.map((payment) => (
                        <tr key={payment.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {new Date(payment.payment_date).toLocaleDateString('es-MX', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {payment.client_name}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {payment.plan_name}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                            ${payment.amount.toLocaleString('es-MX')}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              payment.payment_method === 'cash'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            }`}>
                              {payment.payment_method === 'cash' ? 'Efectivo' : 'Transferencia'}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                            {payment.reference || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/pppoe/clients/${payment.client_id}`)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                            >
                              Ver cliente
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Overdue Tab */}
        {activeTab === 'overdue' && overdue && (
          <div className={`rounded-xl shadow-lg overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Clientes Vencidos ({overdue.overdue_count})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Cliente
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Teléfono
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Mensualidad
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Fecha Vencimiento
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Días Vencido
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {overdue.clients.length === 0 ? (
                    <tr>
                      <td colSpan="6" className={`px-6 py-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        🎉 ¡No hay clientes vencidos!
                      </td>
                    </tr>
                  ) : (
                    overdue.clients.map((client) => (
                      <tr key={client.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {client.name}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {client.phone}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          ${client.monthly_payment.toLocaleString('es-MX')}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {new Date(client.next_due_date).toLocaleDateString('es-MX')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            {client.days_overdue} días
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => navigate(`/pppoe/clients/${client.id}`)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                          >
                            Ver detalles
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PPPoEReportsPage;
